import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CareerService } from '../services/career.service';
import { SharedModule } from '../../../shared/shared.module';
import { FormCardComponent } from '../../../shared/components/form-card/form-card.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { InfoCardComponent } from '@apps/landing/src/app/shared/components/cards/info-card/info-card.component';
import { CompoundCardComponent } from '@apps/landing/src/app/shared/components/cards/compound-card/compound-card.component';
import { HlmSkeletonComponent } from '@spartan-ng/ui-skeleton-helm';
import { colorMap } from '../utils/badge-statuses';
import { finalize, takeUntil } from 'rxjs';
import { ResponseModel } from '@apps/landing/src/app/core/models/response.model';
import { ICareerOfferModel, ISkillModel } from '@apps/landing/src/app/modules/careers/models/careeer.model';
import { PAGE_ROUTES } from '@apps/landing/src/app/shared/constants/routes.enum';
import { formFields, teamDetailsData } from '@apps/landing/src/app/modules/careers/utils/constants';
import { handleResponseMessage, RESPONSE_TYPE } from '@apps/landing/src/app/shared/utils/handle-response-message';
import { ContactFormType } from '@apps/landing/src/app/modules/careers/models/career-form.model';
import { Unsubscribe } from '@apps/landing/src/app/shared/classes/unsubscribe';
import { NgOptimizedImage } from '@angular/common';

@Component({
  selector: 'app-career-details',
  standalone: true,
  imports: [SharedModule, FormCardComponent, InfoCardComponent, CompoundCardComponent, HlmSkeletonComponent, NgOptimizedImage],
  templateUrl: './career-details.component.html',
})
export class CareerDetailsComponent extends Unsubscribe {
  private readonly careerService = inject(CareerService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly loading = signal<boolean>(false);
  protected readonly openPosition = signal<any | null>(null);
  protected contactForm: FormGroup<ContactFormType>;

  protected readonly teamDetailsData = teamDetailsData;
  private readonly handleResponseMessage = handleResponseMessage();
  protected readonly fields = formFields;

  ngOnInit(): void {
    const idFromRoute = this.route.snapshot.paramMap.get('id');

    if (idFromRoute) {
      this.getPositionData(idFromRoute);
    } else {
      this.router.navigate([PAGE_ROUTES.CAREERS]);
    }

    this.initForm();
  }

  private getPositionData(id: string) {
    this.loading.set(true);

    this.careerService
      .getOpenedPositionById(id)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (response: ResponseModel<ICareerOfferModel>) => {
          if (response.status && response.data) {
            this.openPosition.set(this.normalizePositionData(response.data));
          } else {
            this.handleResponseMessage('Failed to get position data', RESPONSE_TYPE.ERROR);
          }
        },
      });
  }

  private normalizePositionData(data: ICareerOfferModel) {
    const type = data.type || 'NotSet';
    const level = data.level || 'NotSet';

    const readableType = type === 'NotSet' ? 'General' : type;

    return {
      ...data,
      title: data.name,
      headerBadges: [
        { title: readableType, color: colorMap[type] || '#6B7280' },
        { title: `${level}-Level`, color: colorMap[level] || '#6B7280' },
      ],
      footerBadges: [
        { title: data.schedule, icon: 'clock-3' },
        { title: data.location, icon: 'briefcase' },
      ],
      hardSkills: data.hardSkills?.map((skill: ISkillModel) => skill.name) || [],
      softSkills: data.softSkills?.map((skill: ISkillModel) => skill.name) || [],
    };
  }

  private initForm() {
    this.contactForm = new FormGroup<ContactFormType>({
      firstName: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
      lastName: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
      email: new FormControl(null, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
      phone: new FormControl(null, [Validators.pattern(/^\+?[0-9]{7,15}$/)]),
      cv: new FormControl(null, [Validators.required]),
      coverLetter: new FormControl(null, [Validators.required]),
      agree: new FormControl(false, [Validators.requiredTrue]),
    });
  }

  protected onSubmit() {
    console.log(this.contactForm.getRawValue());
    this.contactForm.reset();
  }
}
